import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Chapter, Reveal } from "./Reveal";
import { useContent } from "../lib/content";

export const Testimonials = () => {
  const { testimonials } = useContent();
  const [index, setIndex] = useState(0);
  const count = testimonials.length;

  const next = useCallback(() => setIndex((i) => i + 1), []);
  const prev = () => setIndex((i) => i - 1);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  if (!count) return null;
  const t = testimonials[((index % count) + count) % count];
  const activeIdx = ((index % count) + count) % count;

  return (
    <section className="py-24 lg:py-32 bg-[#f8eef6] relative grain" data-testid="testimonials-section">
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10">
        <Chapter number="05" label="Kind Words" />
        <Reveal>
          <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
            <Quote size={44} className="text-[#c9006f]" strokeWidth={1.2} />
            <span
              className="inline-flex items-center gap-2.5 rounded-full bg-white border border-[#c9006f]/20 shadow-sm px-5 py-2.5"
              data-testid="rating-snapshot"
            >
              <span className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-[#c9006f] fill-[#c9006f]" />
                ))}
              </span>
              <span className="text-sm font-bold text-[#121212]" data-testid="rating-snapshot-average">
                {(testimonials.reduce((s, x) => s + (x.rating || 5), 0) / count).toFixed(1)}
              </span>
              <span className="text-sm text-[#121212]/50" data-testid="rating-snapshot-count">
                · {count} client review{count > 1 ? "s" : ""}
              </span>
            </span>
          </div>
        </Reveal>
        <div className="min-h-[240px] md:min-h-[200px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="active-testimonial"
            >
              <p className="font-serif text-[#121212] text-2xl md:text-3xl leading-[1.4] mb-8">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-4">
                <span className="flex gap-0.5" data-testid="testimonial-stars">
                  {[...Array(t.rating || 5)].map((_, i) => (
                    <Star key={i} size={14} className="text-[#c9006f] fill-[#c9006f]" />
                  ))}
                </span>
                <div>
                  <p className="font-semibold text-[#91008d]">{t.name}</p>
                  <p className="text-sm text-[#121212]/50">{t.event}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-center gap-4 mt-10">
          <button
            onClick={prev}
            data-testid="testimonial-prev-btn"
            aria-label="Previous testimonial"
            className="w-11 h-11 rounded-full border border-[#91008d]/25 text-[#91008d] grid place-items-center hover:bg-[#91008d] hover:text-white transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            data-testid="testimonial-next-btn"
            aria-label="Next testimonial"
            className="w-11 h-11 rounded-full border border-[#91008d]/25 text-[#91008d] grid place-items-center hover:bg-[#91008d] hover:text-white transition-colors"
          >
            <ChevronRight size={20} />
          </button>
          <div className="flex gap-2 ml-4">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-400 ${
                  i === activeIdx ? "w-8 bg-[#c9006f]" : "w-3 bg-[#91008d]/20"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
